/**
 * useMapPinNavigation Hook
 *
 * Provides navigation functionality for map pin components in TerraMatrix.
 * Handles click events on map pins and triggers page navigation based on
 * configured mappings - either per-pin or via global default target.
 *
 * Created: February 2026 (Phase 6: Pin Navigation)
 * @see Documents/Roadmaps/Map_Groups_Pins_Navigation_Roadmap.md
 */

import { useCallback, useMemo } from 'react';
import { Component } from '@/types';
import {
  MapPin,
  PinNavigationMapping,
  PinNavigationConfig,
} from '@/components/charts/MapComponent.types';
import {
  NavigationConfig,
  InteractionHandlers,
} from '@/components/shared/rendering/InteractionContext';

/**
 * Interaction object shape from useComponentInteraction hook.
 * We define this locally to avoid circular dependencies.
 */
interface ComponentInteraction {
  canInteract: boolean;
  handlers: Partial<InteractionHandlers>;
  mode: 'editing' | 'viewing' | 'functional' | 'pin-placement';
  capabilities: {
    canSelect: boolean;
    canResize: boolean;
    canDrag: boolean;
    canInteract: boolean;
    canNavigate: boolean;
    canEditProperties: boolean;
    canDelete: boolean;
    canPlacePin?: boolean;
  };
}

/**
 * Return type for the useMapPinNavigation hook
 */
export interface UseMapPinNavigationReturn {
  /** Handle click on a pin - triggers navigation if configured */
  handlePinClick: (pinId: string) => Promise<void>;
  /** Check if a specific pin has navigation configured */
  isNavigable: (pinId: string) => boolean;
  /** Check if any pin has navigation enabled */
  hasAnyNavigation: boolean;
  /** Get the navigation mapping for a specific pin */
  getNavigationForPin: (pinId: string) => PinNavigationMapping | null;
  /** The global pin navigation config */
  pinNavigationConfig: PinNavigationConfig | undefined;
}

/**
 * Props for the useMapPinNavigation hook
 */
export interface UseMapPinNavigationProps {
  /** The map component instance */
  component: Component;
  /** Result from useComponentInteraction hook */
  interaction: ComponentInteraction;
  /** Array of pins on the map */
  pins: MapPin[];
  /** Global pin navigation configuration */
  pinNavigation?: PinNavigationConfig;
}

/**
 * Hook for handling pin navigation in TerraMatrix MapComponent.
 *
 * Reads navigation configuration from pin.navigation or global pinNavigation config
 * and provides handlers for click events. Integrates with InteractionContext for page navigation.
 *
 * @param props - Hook configuration
 * @returns Navigation handlers and state
 *
 * @example
 * ```tsx
 * // In MapComponent.tsx
 * const interaction = useComponentInteraction(component);
 * const { handlePinClick, isNavigable, getNavigationForPin } = useMapPinNavigation({
 *   component,
 *   interaction,
 *   pins,
 *   pinNavigation: properties.pinNavigation,
 * });
 *
 * // On Pin click
 * if (isNavigable(pinId)) {
 *   handlePinClick(pinId);
 * }
 *
 * // For cursor styling
 * cursor={isNavigable(pinId) ? 'pointer' : 'default'}
 * ```
 */
export function useMapPinNavigation({
  component,
  interaction,
  pins,
  pinNavigation,
}: UseMapPinNavigationProps): UseMapPinNavigationReturn {
  /**
   * Determine if pin navigation is enabled globally
   */
  const navigationEnabled = useMemo(() => {
    // If pinNavigation config exists and is explicitly disabled, return false
    if (pinNavigation && pinNavigation.enabled === false) {
      return false;
    }
    // Default to enabled if any pin has navigation or if defaultTarget is set
    return true;
  }, [pinNavigation]);

  /**
   * Check if any pin has navigation configured
   */
  const hasAnyNavigation = useMemo(() => {
    if (!navigationEnabled) return false;

    // Check if global default target is set
    if (pinNavigation?.defaultTarget) return true;

    // Check if any pin has navigation configured
    return pins.some((pin) => pin.navigation?.targetPageId);
  }, [navigationEnabled, pinNavigation?.defaultTarget, pins]);

  /**
   * Find a pin by ID
   */
  const findPin = useCallback(
    (pinId: string): MapPin | undefined => {
      return pins.find((pin) => pin.id === pinId);
    },
    [pins]
  );

  /**
   * Get the navigation mapping for a specific pin.
   *
   * Priority order:
   * 1. Pin-specific navigation config (pin.navigation)
   * 2. Global default target (pinNavigation.defaultTarget)
   * 3. null (no navigation)
   */
  const getNavigationForPin = useCallback(
    (pinId: string): PinNavigationMapping | null => {
      if (!navigationEnabled) {
        console.log('[PinNav] Navigation disabled globally');
        return null;
      }

      const pin = findPin(pinId);
      if (!pin) {
        console.log('[PinNav] Pin not found:', pinId);
        return null;
      }

      // 1. Check pin-specific navigation
      if (pin.navigation?.targetPageId) {
        return pin.navigation;
      }

      // 2. Check global default target
      if (pinNavigation?.defaultTarget) {
        console.log('[PinNav] Using default target for pin:', pinId, '->', pinNavigation.defaultTarget);
        return {
          targetPageId: pinNavigation.defaultTarget,
          cursor: 'pointer',
        };
      }

      // 3. No navigation configured
      console.log('[PinNav] No navigation configured for pin:', pinId);
      return null;
    },
    [navigationEnabled, findPin, pinNavigation?.defaultTarget]
  );

  /**
   * Check if a specific pin is navigable
   */
  const isNavigable = useCallback(
    (pinId: string): boolean => {
      return getNavigationForPin(pinId) !== null;
    },
    [getNavigationForPin]
  );

  /**
   * Build navigation parameters from pin data and mapping configuration.
   *
   * Merges:
   * 1. Static parameters from mapping
   * 2. Pin data (if passPinDataAsParams is true)
   * 3. Applies global parameterMapping transforms
   */
  const buildNavigationParameters = useCallback(
    (pin: MapPin, mapping: PinNavigationMapping): Record<string, unknown> => {
      let parameters: Record<string, unknown> = {};

      // 1. Add static parameters from mapping
      if (mapping.parameters) {
        parameters = { ...mapping.parameters };
      }

      // 2. Add pin data if configured
      const shouldPassPinData = mapping.passPinDataAsParams;

      if (shouldPassPinData) {
        // Include core pin data
        const pinData: Record<string, unknown> = {
          pinId: pin.id,
          pinName: pin.name,
          latitude: pin.latitude,
          longitude: pin.longitude,
        };

        // Include description if present
        if (pin.description) {
          pinData.pinDescription = pin.description;
        }

        // Include group if present
        if (pin.groupId) {
          pinData.groupId = pin.groupId;
        }

        // Include metadata if present
        if (pin.metadata) {
          pinData.metadata = pin.metadata;
        }

        parameters = { ...parameters, ...pinData };
      }

      // 3. Apply global parameter mapping transforms
      const parameterMapping = pinNavigation?.parameterMapping;
      if (parameterMapping) {
        // Map pin properties to parameter names
        const pinDataForMapping: Record<string, unknown> = {
          id: pin.id,
          name: pin.name,
          latitude: pin.latitude,
          longitude: pin.longitude,
          description: pin.description,
          groupId: pin.groupId,
          ...pin.metadata,
        };

        for (const [pinKey, paramKey] of Object.entries(parameterMapping)) {
          if (pinDataForMapping[pinKey] !== undefined) {
            parameters[paramKey] = pinDataForMapping[pinKey];
          }
        }
      }

      return parameters;
    },
    [pinNavigation?.parameterMapping]
  );

  /**
   * Check if navigation conditions are met.
   */
  const checkConditions = useCallback(
    (pin: MapPin, conditions?: PinNavigationMapping['conditions']): boolean => {
      if (!conditions || conditions.length === 0) {
        return true;
      }

      // Build a data object from pin for condition checking
      const pinData: Record<string, unknown> = {
        id: pin.id,
        name: pin.name,
        latitude: pin.latitude,
        longitude: pin.longitude,
        description: pin.description,
        groupId: pin.groupId,
        ...pin.metadata,
      };

      return conditions.every((condition) => {
        const fieldValue = pinData[condition.field];

        switch (condition.operator) {
          case 'equals':
            return fieldValue === condition.value;
          case 'contains':
            return String(fieldValue).includes(String(condition.value));
          case 'greater':
            return Number(fieldValue) > Number(condition.value);
          case 'less':
            return Number(fieldValue) < Number(condition.value);
          default:
            return true;
        }
      });
    },
    []
  );

  /**
   * Handle click events from map pins.
   * Finds the matching navigation mapping and triggers page navigation.
   */
  const handlePinClick = useCallback(
    async (pinId: string): Promise<void> => {
      console.log('[PinNav] Click received:', {
        componentId: component.id,
        pinId,
        mode: interaction.mode,
      });

      // Check if navigation handler is available
      if (!interaction.handlers.onNavigate) {
        console.log('[PinNav] No onNavigate handler available (not in functional mode?)');
        return;
      }

      // Check if we're in functional mode (navigation only works there)
      if (interaction.mode !== 'functional') {
        console.log('[PinNav] Not in functional mode, skipping navigation');
        return;
      }

      // Check if navigation is enabled
      if (!navigationEnabled) {
        console.log('[PinNav] Pin navigation not enabled for component:', component.id);
        return;
      }

      // Find the pin
      const pin = findPin(pinId);
      if (!pin) {
        console.log('[PinNav] Pin not found:', pinId);
        return;
      }

      // Get the navigation mapping for this pin
      const mapping = getNavigationForPin(pinId);
      if (!mapping) {
        console.log('[PinNav] No navigation mapping found for pin:', pinId);
        return;
      }

      // Check conditions
      if (!checkConditions(pin, mapping.conditions)) {
        console.log('[PinNav] Conditions not met, skipping navigation');
        return;
      }

      // Build parameters
      const parameters = buildNavigationParameters(pin, mapping);

      console.log('[PinNav] Navigating:', {
        pinId,
        pinName: pin.name,
        targetPageId: mapping.targetPageId,
        parameters,
        transition: mapping.transition,
      });

      // Build NavigationConfig for the handler
      const navConfig: NavigationConfig = {
        targetPageId: mapping.targetPageId,
        parameters,
        transition: mapping.transition,
        conditions: mapping.conditions,
      };

      // Trigger navigation
      try {
        const result = await interaction.handlers.onNavigate(navConfig);
        console.log('[PinNav] Navigation result:', result);
      } catch (error) {
        console.error('[PinNav] Navigation error:', error);
      }
    },
    [
      component.id,
      interaction.mode,
      interaction.handlers,
      navigationEnabled,
      findPin,
      getNavigationForPin,
      checkConditions,
      buildNavigationParameters,
    ]
  );

  return {
    handlePinClick,
    isNavigable,
    hasAnyNavigation,
    getNavigationForPin,
    pinNavigationConfig: pinNavigation,
  };
}

export default useMapPinNavigation;
